import { jsx as _jsx } from "react/jsx-runtime";
import { useQuery } from '@apollo/client';
import { Autocomplete, } from '@guildeducationinc/recess';
import { COUNTRIES } from '@src/graphql/queries/lookup';
import { DropdownSelect, useLocale } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
/**
 * A component that allows the user to select multiple countries.
 *
 * @component
 * @param {CountrySelectProps} props - The component props.
 * @param {string} props.className - The CSS class name for the component.
 * @param {string} props.label - The label for the component.
 * @param {string} props.value - The selected country.
 * @param {string[]} props.errors - The errors associated with the component.
 * @param {Function} props.onChange - The callback function to handle changes in the selected countries.
 * @param {boolean} props.includeInactive - Whether to include inactive countries.
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * ```tsx
 * <CountrySelect
 *   className="country-select"
 *   label="Select countries"
 *   value={selectedcountries}
 *   errors={countryErrors}
 *   onChange={handlecountryChange}
 *   includeInactive
 *   options={countryOptions}
 * />
 * ```
 *
 */
export const CountrySelect = ({ className, label, value, errors, onChange, includeInactive = false, options, supportEmptyState = false, }) => {
    const { data } = useQuery(COUNTRIES, {
        variables: { includeInactive },
        skip: Boolean(options),
    });
    const { t } = useLocale();
    const computedLabel = label ?? t('Country');
    const baseOptions = useMemo(() => options ?? (data?.countries || []), [options, data]);
    const countryOptions = useMemo(() => {
        if (supportEmptyState) {
            return [{ value: '', label: t('Country not specified') }, ...(baseOptions || [])];
        }
        return baseOptions || [];
    }, [baseOptions, supportEmptyState]);
    const [selectedCountry, setSelectedCountry] = useState();
    useEffect(() => {
        if (countryOptions) {
            const country = countryOptions.find(opt => opt.value === value);
            setSelectedCountry(country);
        }
    }, [countryOptions, value]);
    const handleChange = (e) => {
        onChange(e.selection?.value?.toString() || '');
        setSelectedCountry(e.selection || undefined);
    };
    /**
     * Handles the loading of options for the select dropdown.
     *
     * @param {string} query - The search query.
     * @param {Function} callback - The callback function to receive the matching options.
     * @returns {void}
     */
    const handleLoad = (query, callback) => {
        const sourceOptions = countryOptions || [];
        if (!sourceOptions.length) {
            callback([]);
            return;
        }
        const matches = sourceOptions.filter(opt => {
            if (!opt.label)
                return false;
            return opt.label.toLowerCase().includes((query || '').toLowerCase());
        });
        callback(matches);
    };
    if (includeInactive) {
        return (_jsx(Autocomplete, { easyPortal: true, className: className, label: computedLabel, loadOptions: handleLoad, defaultOptions: true, value: selectedCountry, onChange: handleChange, cacheOptions: true, errors: errors }));
    }
    return (_jsx(DropdownSelect, { className: className, label: computedLabel, value: value, options: countryOptions, onChange: handleChange, errors: errors }));
};
